import type {
  InventoryPolicyControlDashboardPayload,
  InventoryPolicyControlScope,
  ProblemAffectedPolicyRow,
  ProblemPolicySuggestedChange,
} from '../model/controlCenter.types';

function normalizeSuggestedAction(action: string): string {
  return action.toLowerCase().normalize('NFD').replace(/[\u0300-\u036f]/g, '');
}

function inferSuggestedChange(policy: ProblemAffectedPolicyRow): ProblemPolicySuggestedChange | undefined {
  const action = normalizeSuggestedAction(policy.suggestedAction);
  let match: RegExpMatchArray | null = null;

  match = action.match(/^subir rop a (\d+) ud$/);
  if (match) {
    return {
      field: 'reorderPoint',
      currentValue: policy.reorderPoint,
      suggestedValue: Number(match[1]),
      unit: 'ud',
    };
  }

  match = action.match(/^subir rop de (\d+) a (\d+) ud$/);
  if (match) {
    return {
      field: 'reorderPoint',
      currentValue: Number(match[1]),
      suggestedValue: Number(match[2]),
      unit: 'ud',
    };
  }

  match = action.match(/^(subir|bajar) ss de (\d+) a (\d+) ud$/);
  if (match) {
    return {
      field: 'safetyStock',
      currentValue: Number(match[2]),
      suggestedValue: Number(match[3]),
      unit: 'ud',
    };
  }

  match = action.match(/^(subir|bajar|reducir) minimo de (\d+) a (\d+) ud$/);
  if (match) {
    return {
      field: 'minimumStock',
      currentValue: Number(match[2]),
      suggestedValue: Number(match[3]),
      unit: 'ud',
    };
  }

  match = action.match(/^reducir minimo a (\d+) ud$/);
  if (match) {
    return {
      field: 'minimumStock',
      currentValue: policy.reorderPoint,
      suggestedValue: Number(match[1]),
      unit: 'ud',
    };
  }

  match = action.match(/^(subir|reducir) cobertura de (\d+) a (\d+) dias$/);
  if (match) {
    return {
      field: 'coverageDays',
      currentValue: Number(match[2]),
      suggestedValue: Number(match[3]),
      unit: 'dias',
    };
  }

  match = action.match(/^reducir cobertura objetivo de (\d+) a (\d+) dias$/);
  if (match) {
    return {
      field: 'coverageTargetDays',
      currentValue: Number(match[1]),
      suggestedValue: Number(match[2]),
      unit: 'dias',
    };
  }

  match = action.match(/^reducir frecuencia(?: de compra)? de (\d+) a (\d+) dias$/);
  if (match) {
    return {
      field: 'purchaseFrequencyDays',
      currentValue: Number(match[1]),
      suggestedValue: Number(match[2]),
      unit: 'dias',
    };
  }

  match = action.match(/^(subir|bajar) frecuencia de reposicion de (.+) a (.+)$/);
  if (match) {
    const currentValue = match[2];
    const suggestedValue = match[3];
    const inferredFrequencyUnit =
      /envios\/semana$/.test(suggestedValue)
        ? 'envios/semana'
        : /veces\/semana$/.test(suggestedValue)
          ? 'veces/semana'
          : null;

    return {
      field: 'replenishmentFrequency',
      currentValue:
        inferredFrequencyUnit != null && /^\d+$/.test(currentValue)
          ? `${currentValue} ${inferredFrequencyUnit}`
          : currentValue,
      suggestedValue,
    };
  }

  match = action.match(/^reducir moq (?:aplicado|efectivo) de (\d+) a (\d+) ud$/);
  if (match) {
    return {
      field: 'effectiveMoq',
      currentValue: Number(match[1]),
      suggestedValue: Number(match[2]),
      unit: 'ud',
    };
  }

  match = action.match(/^reducir lote de compra de (\d+) a (\d+) ud$/);
  if (match) {
    return {
      field: 'purchaseLot',
      currentValue: Number(match[1]),
      suggestedValue: Number(match[2]),
      unit: 'ud',
    };
  }

  match = action.match(/^reducir multiplo de compra de (\d+) a (\d+) ud$/);
  if (match) {
    return {
      field: 'purchaseMultiple',
      currentValue: Number(match[1]),
      suggestedValue: Number(match[2]),
      unit: 'ud',
    };
  }

  return undefined;
}

function attachSuggestedChanges(payload: InventoryPolicyControlDashboardPayload): void {
  payload.problemInsights.forEach((problem) => {
    problem.affectedPolicies.forEach((policy) => {
      policy.suggestedChange = inferSuggestedChange(policy);
    });
  });
}

/**
 * Datos de demostración con la misma forma que tendrá la respuesta API.
 * Los KPI de inventario reutilizan la semántica de `PolicyCalculations`.
 */
export const inventoryPolicyControlDashboardMock: InventoryPolicyControlDashboardPayload = {
  kpi: {
    id: 'snap-mock-1',
    organizationId: 1,
    warehouseId: null,
    snapshotAt: new Date().toISOString(),
    totalInventoryValueEur: 2_430_553,
    monthsOfInventory: 83.43,
    skusNoStockCount: 5_743,
    skusOverstockCount: 1_509,
    policiesToReviewCount: 382,
    totalInventoryValuePctVsPrev: 8.2,
    monthsOfInventoryPctVsPrev: 12.1,
    skusNoStockPctVsPrev: 15.6,
    skusOverstockPctVsPrev: 6.3,
  },
  health: {
    overallScore: 62,
    overallLabelKey: 'regular',
    segments: [
      { statusKey: 'obsolete', statusLabel: 'Obsoleto', statusOrder: 1, percentage: 51, count: 0 },
      { statusKey: 'overstock', statusLabel: 'Sobreinventario', statusOrder: 2, percentage: 10, count: 0 },
      { statusKey: 'optimal', statusLabel: 'Óptimo', statusOrder: 3, percentage: 3, count: 0 },
      { statusKey: 'under_minimum', statusLabel: 'Bajo mínimos', statusOrder: 4, percentage: 6, count: 0 },
      { statusKey: 'no_stock', statusLabel: 'Sin stock', statusOrder: 5, percentage: 30, count: 0 },
    ],
  },
  topProblems: [
    {
      id: 'tp-1',
      problemCode: 'ROP-001',
      problemLabel: 'ROP bajo',
      affectedPoliciesCount: 312,
      economicImpact: 18_400,
      impactLabel: '18.400 € venta perdida',
      priority: 'alta',
      detectionCriteria: 'ROP actual < ROP sugerido',
      probableCause: 'ROP actual por debajo del nivel necesario para cubrir lead time y stock de seguridad',
      recommendedAction: 'Subir ROP y revisar stock de seguridad o lead time',
    },
    {
      id: 'tp-2',
      problemCode: 'SS-003',
      problemLabel: 'SS excesivo',
      affectedPoliciesCount: 428,
      economicImpact: 221_000,
      impactLabel: '221.000 € inventario exceso',
      priority: 'alta',
      detectionCriteria: 'SS actual > SS sugerido',
      probableCause: 'Safety stock actual superior al sugerido para la variabilidad real',
      recommendedAction: 'Reducir SS o recalibrar la regla de servicio',
    },
    {
      id: 'tp-3',
      problemCode: 'OPS-001',
      problemLabel: 'Pedidos manuales recurrentes',
      affectedPoliciesCount: 89,
      economicImpact: 32_000,
      impactLabel: '32.000 € riesgo operativo',
      priority: 'alta',
      detectionCriteria: 'Pedidos manuales >= umbral con política automática activa',
      probableCause: 'La política no recoge una restricción operativa o demanda no capturada',
      recommendedAction: 'Revisar ROP, forecast o restricciones no modeladas',
    },
    {
      id: 'tp-4',
      problemCode: 'FREQ-004',
      problemLabel: 'Cobertura objetivo incoherente',
      affectedPoliciesCount: 190,
      economicImpact: 87_000,
      impactLabel: '87.000 € exceso',
      priority: 'media',
      detectionCriteria: 'Cobertura objetivo fuera de límites aceptables',
      probableCause: 'La combinación de frecuencia, lead time y stock de seguridad genera una cobertura incoherente',
      recommendedAction: 'Ajustar frecuencia y recalcular cobertura objetivo',
    },
    {
      id: 'tp-5',
      problemCode: 'PROV-003',
      problemLabel: 'MOQ genera exceso',
      affectedPoliciesCount: 76,
      economicImpact: 42_000,
      impactLabel: '42.000 € exceso',
      priority: 'media',
      detectionCriteria: 'MOQ >> sugerencia base',
      probableCause: 'El MOQ del proveedor distorsiona la cantidad sugerida base',
      recommendedAction: 'Revisar MOQ o agrupar demanda',
    },
  ],
  problemInsights: [
    {
      id: 'tp-1',
      problemCode: 'ROP-001',
      problemLabel: 'ROP bajo',
      priority: 'alta',
      affectedPoliciesCount: 312,
      estimatedEconomicImpact: 18_400,
      estimatedImpactLabel: '18.400 € venta perdida',
      summary: 'El punto de reorden está activando la reposición demasiado tarde para cubrir la demanda durante el lead time.',
      detectionCriteria: 'ROP actual < ROP sugerido.',
      primaryCause: 'ROP actual por debajo del nivel necesario para cubrir la demanda durante lead time.',
      secondaryCauses: [
        'Lead time real superior al configurado',
        'Safety stock insuficiente',
        'Forecast por debajo de la demanda real',
      ],
      recommendedAction: 'Subir ROP y revisar stock de seguridad o lead time.',
      simulationPossible: true,
      relatedRecommendationIds: ['rec-2', 'rec-3'],
      concentrationDimensions: [
        { id: 'rop-dim-1', dimensionLabelKey: 'family', criticalElement: 'Frenos', impactLabel: '18.400 €' },
        { id: 'rop-dim-2', dimensionLabelKey: 'pointOfSale', criticalElement: 'Valencia', impactLabel: '96 SKUs' },
        { id: 'rop-dim-3', dimensionLabelKey: 'abcXyz', criticalElement: 'A / Z', impactLabel: '42 SKUs' },
      ],
      affectedPolicies: [
        {
          id: 'rop-pol-1',
          productCode: 'AC1080017',
          productName: 'Collare TZD 80 AKZ',
          pointOfSale: 'Valencia',
          supplier: 'Metal Group',
          currentStock: 4,
          reorderPoint: 12,
          safetyStock: 4,
          leadTimeDays: 27,
          suggestedAction: 'Subir ROP a 28 ud',
        },
        {
          id: 'rop-pol-2',
          productCode: 'FR92831',
          productName: 'Disco freno ventilado 280',
          pointOfSale: 'Madrid',
          supplier: 'BrakeFast',
          currentStock: 9,
          reorderPoint: 15,
          safetyStock: 5,
          leadTimeDays: 21,
          suggestedAction: 'Subir SS de 5 a 8 ud',
        },
        {
          id: 'rop-pol-3',
          productCode: 'FR77410',
          productName: 'Pastilla freno ceramica',
          pointOfSale: 'Valencia',
          supplier: 'BrakeFast',
          currentStock: 6,
          reorderPoint: 10,
          safetyStock: 3,
          leadTimeDays: 18,
          suggestedAction: 'Subir ROP de 10 a 14 ud',
        },
      ],
    },
    {
      id: 'tp-2',
      problemCode: 'SS-003',
      problemLabel: 'SS excesivo',
      priority: 'alta',
      affectedPoliciesCount: 428,
      estimatedEconomicImpact: 221_000,
      estimatedImpactLabel: '221.000 € inventario exceso',
      summary: 'La protección definida representa demasiados días de demanda y está inmovilizando capital sin mejorar servicio.',
      detectionCriteria: 'Safety stock actual > safety stock sugerido.',
      primaryCause: 'Safety stock actual superior al sugerido para la variabilidad y el nivel de servicio reales.',
      secondaryCauses: [
        'Perfil de servicio demasiado conservador',
        'Forecast superior a la venta real',
        'Variabilidad sobreestimada',
      ],
      recommendedAction: 'Reducir safety stock o recalibrar la regla de servicio.',
      simulationPossible: true,
      relatedRecommendationIds: ['rec-1'],
      concentrationDimensions: [
        { id: 'ss-dim-1', dimensionLabelKey: 'supplier', criticalElement: 'Metal Group', impactLabel: '221.000 €' },
        { id: 'ss-dim-2', dimensionLabelKey: 'family', criticalElement: 'Herramientas', impactLabel: '110.000 €' },
        { id: 'ss-dim-3', dimensionLabelKey: 'product', criticalElement: 'Consumibles A', impactLabel: '67.200 €' },
      ],
      affectedPolicies: [
        {
          id: 'ss-pol-1',
          productCode: 'MT55001',
          productName: 'Perfil aluminio 2m',
          pointOfSale: 'Central',
          supplier: 'Metal Group',
          currentStock: 840,
          reorderPoint: 120,
          safetyStock: 75,
          leadTimeDays: 18,
          suggestedAction: 'Bajar SS de 75 a 28 ud',
        },
        {
          id: 'ss-pol-2',
          productCode: 'MT55290',
          productName: 'Escuadra galvanizada',
          pointOfSale: 'Central',
          supplier: 'Metal Group',
          currentStock: 610,
          reorderPoint: 95,
          safetyStock: 68,
          leadTimeDays: 16,
          suggestedAction: 'Bajar SS de 68 a 32 ud',
        },
        {
          id: 'ss-pol-3',
          productCode: 'HR88120',
          productName: 'Juego llaves allen',
          pointOfSale: 'Bilbao',
          supplier: 'ToolBase',
          currentStock: 420,
          reorderPoint: 70,
          safetyStock: 61,
          leadTimeDays: 14,
          suggestedAction: 'Bajar SS de 61 a 24 ud',
        },
      ],
    },
    {
      id: 'tp-3',
      problemCode: 'OPS-001',
      problemLabel: 'Pedidos manuales recurrentes',
      priority: 'alta',
      affectedPoliciesCount: 89,
      estimatedEconomicImpact: 32_000,
      estimatedImpactLabel: '32.000 € riesgo operativo',
      summary: 'El comprador está generando pedidos fuera de la sugerencia automática con frecuencia, señal de que la política no recoge toda la realidad operativa.',
      detectionCriteria: 'Pedidos manuales >= umbral con política automática activa.',
      primaryCause: 'La política automática no está recogiendo una restricción operativa o una demanda no modelada.',
      secondaryCauses: [
        'ROP bajo',
        'MOQ no modelado',
        'Forecast insuficiente',
      ],
      recommendedAction: 'Revisar ROP, forecast o restricciones no modeladas.',
      simulationPossible: false,
      relatedRecommendationIds: ['rec-4'],
      concentrationDimensions: [
        { id: 'ops-dim-1', dimensionLabelKey: 'buyer', criticalElement: 'Laura', impactLabel: '31 pedidos' },
        { id: 'ops-dim-2', dimensionLabelKey: 'supplier', criticalElement: 'Supplier X', impactLabel: '14 pedidos' },
        { id: 'ops-dim-3', dimensionLabelKey: 'pointOfSale', criticalElement: 'Valencia', impactLabel: '22 pedidos' },
      ],
      affectedPolicies: [
        {
          id: 'ops-pol-1',
          productCode: 'CM33010',
          productName: 'Componente fijacion M8',
          pointOfSale: 'Valencia',
          supplier: 'Supplier X',
          currentStock: 31,
          reorderPoint: 18,
          safetyStock: 6,
          leadTimeDays: 24,
          suggestedAction: 'Subir ROP de 18 a 22 ud',
        },
        {
          id: 'ops-pol-2',
          productCode: 'PK11090',
          productName: 'Kit montaje premium',
          pointOfSale: 'Madrid',
          supplier: 'Assemblix',
          currentStock: 12,
          reorderPoint: 8,
          safetyStock: 2,
          leadTimeDays: 19,
          suggestedAction: 'Subir ROP de 8 a 11 ud',
        },
        {
          id: 'ops-pol-3',
          productCode: 'CM41220',
          productName: 'Tornillo inox A2',
          pointOfSale: 'Valencia',
          supplier: 'Supplier X',
          currentStock: 55,
          reorderPoint: 22,
          safetyStock: 7,
          leadTimeDays: 28,
          suggestedAction: 'Reducir MOQ aplicado de 500 a 250 ud',
        },
      ],
    },
    {
      id: 'tp-4',
      problemCode: 'FREQ-004',
      problemLabel: 'Cobertura objetivo incoherente',
      priority: 'media',
      affectedPoliciesCount: 190,
      estimatedEconomicImpact: 87_000,
      estimatedImpactLabel: '87.000 € exceso',
      summary: 'La combinación de frecuencia, lead time y safety stock genera una cobertura objetivo fuera del rango razonable.',
      detectionCriteria: 'Cobertura objetivo fuera de los límites aceptables.',
      primaryCause: 'La combinación de frecuencia, lead time y stock de seguridad genera una cobertura objetivo incoherente.',
      secondaryCauses: [
        'Frecuencia demasiado amplia',
        'MOQ alto',
        'Lead time configurado de forma conservadora',
      ],
      recommendedAction: 'Ajustar frecuencia y recalcular cobertura objetivo.',
      simulationPossible: true,
      relatedRecommendationIds: ['rec-1', 'rec-4'],
      concentrationDimensions: [
        { id: 'freq-dim-1', dimensionLabelKey: 'supplier', criticalElement: 'Metal Group', impactLabel: '87.000 €' },
        { id: 'freq-dim-2', dimensionLabelKey: 'family', criticalElement: 'Herramientas', impactLabel: '51 SKUs' },
        { id: 'freq-dim-3', dimensionLabelKey: 'buyer', criticalElement: 'Laura', impactLabel: '19 politicas' },
      ],
      affectedPolicies: [
        {
          id: 'freq-pol-1',
          productCode: 'MT44100',
          productName: 'Placa acero 4mm',
          pointOfSale: 'Central',
          supplier: 'Metal Group',
          currentStock: 390,
          reorderPoint: 82,
          safetyStock: 21,
          leadTimeDays: 30,
          suggestedAction: 'Reducir frecuencia de 45 a 21 dias',
        },
        {
          id: 'freq-pol-2',
          productCode: 'HT88310',
          productName: 'Llave dinamometrica',
          pointOfSale: 'Bilbao',
          supplier: 'ToolBase',
          currentStock: 74,
          reorderPoint: 15,
          safetyStock: 8,
          leadTimeDays: 17,
          suggestedAction: 'Reducir cobertura objetivo de 34 a 21 dias',
        },
        {
          id: 'freq-pol-3',
          productCode: 'MT44771',
          productName: 'Pletina aluminio 5mm',
          pointOfSale: 'Central',
          supplier: 'Metal Group',
          currentStock: 280,
          reorderPoint: 60,
          safetyStock: 18,
          leadTimeDays: 28,
          suggestedAction: 'Reducir frecuencia de compra de 42 a 28 dias',
        },
      ],
    },
    {
      id: 'tp-5',
      problemCode: 'PROV-003',
      problemLabel: 'MOQ genera exceso',
      priority: 'media',
      affectedPoliciesCount: 76,
      estimatedEconomicImpact: 42_000,
      estimatedImpactLabel: '42.000 € exceso',
      summary: 'La política base calcula una necesidad razonable, pero la restricción logística del proveedor obliga a comprar mucho más.',
      detectionCriteria: 'MOQ > cantidad sugerida base por encima del umbral.',
      primaryCause: 'El MOQ del proveedor obliga a comprar bastante más de lo que la política base necesita.',
      secondaryCauses: [
        'Múltiplos de compra elevados',
        'Demanda irregular',
        'Falta de agrupación de demanda',
      ],
      recommendedAction: 'Revisar MOQ o agrupar demanda antes de aplicar cambios permanentes.',
      simulationPossible: true,
      relatedRecommendationIds: ['rec-4'],
      concentrationDimensions: [
        { id: 'prov-dim-1', dimensionLabelKey: 'supplier', criticalElement: 'Supplier X', impactLabel: '42.000 €' },
        { id: 'prov-dim-2', dimensionLabelKey: 'product', criticalElement: 'Componentes comunes', impactLabel: '12 BOMs' },
        { id: 'prov-dim-3', dimensionLabelKey: 'pointOfSale', criticalElement: 'Valencia', impactLabel: '19 politicas' },
      ],
      affectedPolicies: [
        {
          id: 'prov-pol-1',
          productCode: 'CP20330',
          productName: 'Conector rapido 12mm',
          pointOfSale: 'Valencia',
          supplier: 'Supplier X',
          currentStock: 180,
          reorderPoint: 22,
          safetyStock: 7,
          leadTimeDays: 32,
          suggestedAction: 'Reducir MOQ efectivo de 500 a 250 ud',
        },
        {
          id: 'prov-pol-2',
          productCode: 'CP20410',
          productName: 'Abrazadera inox 18mm',
          pointOfSale: 'Valencia',
          supplier: 'Supplier X',
          currentStock: 210,
          reorderPoint: 24,
          safetyStock: 8,
          leadTimeDays: 29,
          suggestedAction: 'Reducir lote de compra de 400 a 200 ud',
        },
        {
          id: 'prov-pol-3',
          productCode: 'CP22005',
          productName: 'Terminal cableado A5',
          pointOfSale: 'Madrid',
          supplier: 'Supplier X',
          currentStock: 140,
          reorderPoint: 20,
          safetyStock: 6,
          leadTimeDays: 31,
          suggestedAction: 'Reducir multiplo de compra de 100 a 50 ud',
        },
      ],
    },
  ],
  dimensions: [
    {
      id: 'dim-1',
      dimensionKey: 'supplier',
      dimensionLabelKey: 'supplier',
      healthLabel: 'mala',
      mainProblem: 'Sobrestock + frecuencia amplia',
      alertCount: 142,
      impactValue: 221_000,
      impactUnit: 'eur',
    },
    {
      id: 'dim-2',
      dimensionKey: 'family',
      dimensionLabelKey: 'family',
      healthLabel: 'regular',
      mainProblem: 'Forecast bajo + roturas',
      alertCount: 93,
      impactValue: 110_000,
      impactUnit: 'eur',
    },
    {
      id: 'dim-3',
      dimensionKey: 'segment',
      dimensionLabelKey: 'salesPotential',
      healthLabel: 'regular',
      mainProblem: 'Cobertura desalineada por potencial de venta',
      alertCount: 76,
      impactValue: 90_500,
      impactUnit: 'eur',
    },
    {
      id: 'dim-4',
      dimensionKey: 'product',
      dimensionLabelKey: 'product',
      healthLabel: 'regular',
      mainProblem: 'Pedidos manuales y desviaciones de parametros',
      alertCount: 65,
      impactValue: 67_200,
      impactUnit: 'eur',
    },
    {
      id: 'dim-5',
      dimensionKey: 'abc_xyz',
      dimensionLabelKey: 'abcXyz',
      healthLabel: 'buena',
      mainProblem: 'Desviacion de safety stock en segmentos criticos',
      alertCount: null,
      impactValue: null,
      impactUnit: 'skus',
    },
  ],
  recommendationsInbox: [
    {
      id: 'rec-inbox-1',
      sortOrder: 1,
      title: 'Revisar regla de proveedor · Proveedor · Metal Group',
      priority: 'alta',
      recommendationId: 'rec-1',
      sourceAnomalyCodes: ['EXC-001', 'FREQ-004', 'PROV-003'],
      status: 'pending_approval',
      scopeLabel: 'Proveedor · Metal Group',
      expectedImpactLabel: 'Menor capital inmovilizado y compras más estables',
      createdAt: new Date(Date.now() - 34 * 60_000).toISOString(),
    },
    {
      id: 'rec-inbox-2',
      sortOrder: 2,
      title: 'Recalibrar política · Segmento · A/Z',
      priority: 'media',
      recommendationId: 'rec-2',
      sourceAnomalyCodes: ['SS-001', 'SERV-002'],
      status: 'draft',
      scopeLabel: 'Segmento · A/Z',
      expectedImpactLabel: 'Reducir roturas futuras en productos críticos',
      createdAt: new Date(Date.now() - 60 * 60_000).toISOString(),
    },
    {
      id: 'rec-inbox-3',
      sortOrder: 3,
      title: 'Revisar forecast · Familia · Frenos',
      priority: 'media',
      recommendationId: 'rec-3',
      sourceAnomalyCodes: ['DEM-001', 'SERV-004'],
      status: 'monitoring',
      scopeLabel: 'Familia · Frenos',
      expectedImpactLabel: 'Alinear forecast y bajar venta perdida',
      createdAt: new Date(Date.now() - 2 * 60 * 60_000).toISOString(),
    },
    {
      id: 'rec-inbox-4',
      sortOrder: 4,
      title: 'Revisar regla de proveedor · Proveedor · Supplier X',
      priority: 'baja',
      recommendationId: 'rec-4',
      sourceAnomalyCodes: ['PROV-003', 'PROV-004'],
      status: 'approved',
      scopeLabel: 'Proveedor · Supplier X',
      expectedImpactLabel: 'Reducir sobrecompra por restricciones logísticas',
      createdAt: new Date(Date.now() - 5 * 60 * 60_000).toISOString(),
    },
  ],
  anomalyRecords: [
    {
      id: 'an-1',
      organizationId: 1,
      warehouseId: 1,
      productId: 1001,
      supplierId: 301,
      familyId: 41,
      scopeType: 'product',
      scopeId: '1001|1|301',
      itemRole: 'finished_product',
      currentPolicyMode: 'stock',
      suggestedPolicyMode: 'stock',
      anomalyCode: 'ROP-001',
      anomalyCategory: 'rop',
      severity: 'alta',
      riskScore: 94,
      impactType: 'service',
      primaryCause: 'ROP actual por debajo del nivel sugerido',
      secondaryCauses: ['Lead time real superior al configurado', 'Venta perdida recurrente'],
      confidenceScore: 0.89,
      detectedAt: new Date().toISOString(),
      status: 'open',
      currentValue: 'ROP actual 12 ud',
      expectedValue: 'ROP sugerido 28 ud',
      differenceValue: '-16 ud',
      economicImpact: 18_400,
      serviceImpact: 12,
      operationalImpact: 4,
      productionImpact: null,
      blockedOrderValue: null,
      affectedProductionOrdersCount: null,
      affectedSalesOrdersCount: 7,
      bomUsageCount: null,
      recommendedAction: 'Aumentar ROP y revisar lead time/safety stock',
      simulationAvailable: true,
    },
    {
      id: 'an-2',
      organizationId: 1,
      warehouseId: 1,
      productId: 1025,
      supplierId: 301,
      familyId: 52,
      scopeType: 'product',
      scopeId: '1025|1|301',
      itemRole: 'finished_product',
      currentPolicyMode: 'stock',
      suggestedPolicyMode: 'stock',
      anomalyCode: 'SS-003',
      anomalyCategory: 'safety_stock',
      severity: 'alta',
      riskScore: 91,
      impactType: 'capital',
      primaryCause: 'Safety stock sobredimensionado frente a demanda real',
      secondaryCauses: ['Forecast alto respecto a venta real', 'Perfil de servicio demasiado conservador'],
      confidenceScore: 0.86,
      detectedAt: new Date().toISOString(),
      status: 'reviewed',
      currentValue: 'SS actual 75 días',
      expectedValue: 'SS sugerido 28 días',
      differenceValue: '+47 días',
      economicImpact: 221_000,
      serviceImpact: 0,
      operationalImpact: 1,
      productionImpact: null,
      blockedOrderValue: null,
      affectedProductionOrdersCount: null,
      affectedSalesOrdersCount: 0,
      bomUsageCount: null,
      recommendedAction: 'Reducir safety stock y revisar forecast',
      simulationAvailable: true,
    },
    {
      id: 'an-3',
      organizationId: 1,
      warehouseId: 2,
      productId: 1098,
      supplierId: 415,
      familyId: 41,
      scopeType: 'product',
      scopeId: '1098|2|415',
      itemRole: 'finished_product',
      currentPolicyMode: 'stock',
      suggestedPolicyMode: 'stock',
      anomalyCode: 'OPS-001',
      anomalyCategory: 'operational_behavior',
      severity: 'alta',
      riskScore: 76,
      impactType: 'operational',
      primaryCause: 'Pedidos manuales recurrentes fuera de la sugerencia automática',
      secondaryCauses: ['ROP bajo', 'MOQ no modelado'],
      confidenceScore: 0.82,
      detectedAt: new Date().toISOString(),
      status: 'open',
      currentValue: '9 pedidos manuales / 60 días',
      expectedValue: '0-1 pedidos manuales / 60 días',
      differenceValue: '+8 pedidos',
      economicImpact: 32_000,
      serviceImpact: 3,
      operationalImpact: 9,
      productionImpact: null,
      blockedOrderValue: null,
      affectedProductionOrdersCount: null,
      affectedSalesOrdersCount: 2,
      bomUsageCount: null,
      recommendedAction: 'Analizar patrón manual y recalibrar parámetros',
      simulationAvailable: false,
    },
    {
      id: 'an-4',
      organizationId: 1,
      warehouseId: 1,
      productId: null,
      supplierId: 301,
      familyId: null,
      scopeType: 'supplier',
      scopeId: '301',
      itemRole: 'raw_material',
      currentPolicyMode: 'stock',
      suggestedPolicyMode: 'stock',
      anomalyCode: 'FREQ-004',
      anomalyCategory: 'frequency_coverage',
      severity: 'media',
      riskScore: 71,
      impactType: 'capital',
      primaryCause: 'Cobertura objetivo incoherente por frecuencia demasiado amplia',
      secondaryCauses: ['MOQ alto', 'Lead time conservador'],
      confidenceScore: 0.78,
      detectedAt: new Date().toISOString(),
      status: 'open',
      currentValue: 'Cobertura objetivo 154 días',
      expectedValue: 'Cobertura objetivo 65 días',
      differenceValue: '+89 días',
      economicImpact: 87_000,
      serviceImpact: 0,
      operationalImpact: 3,
      productionImpact: null,
      blockedOrderValue: null,
      affectedProductionOrdersCount: null,
      affectedSalesOrdersCount: null,
      bomUsageCount: null,
      recommendedAction: 'Reducir frecuencia o revisar calendario del proveedor',
      simulationAvailable: true,
    },
    {
      id: 'an-5',
      organizationId: 1,
      warehouseId: 1,
      productId: 2033,
      supplierId: 512,
      familyId: 73,
      scopeType: 'product',
      scopeId: '2033|1|512',
      itemRole: 'component',
      currentPolicyMode: 'bajo_pedido',
      suggestedPolicyMode: 'stock_temporal',
      anomalyCode: 'PROV-003',
      anomalyCategory: 'supplier_constraints',
      severity: 'media',
      riskScore: 54,
      impactType: 'supplier',
      primaryCause: 'MOQ obliga a triplicar la sugerencia base',
      secondaryCauses: ['Proveedor con lote mínimo alto', 'Consumo irregular'],
      confidenceScore: 0.76,
      detectedAt: new Date().toISOString(),
      status: 'open',
      currentValue: 'MOQ 600 ud',
      expectedValue: 'Sugerencia base 180 ud',
      differenceValue: '+420 ud',
      economicImpact: 42_000,
      serviceImpact: 0,
      operationalImpact: 2,
      productionImpact: 1,
      blockedOrderValue: 14_500,
      affectedProductionOrdersCount: 2,
      affectedSalesOrdersCount: 0,
      bomUsageCount: 12,
      recommendedAction: 'Agrupar demanda o renegociar MOQ',
      simulationAvailable: true,
    },
  ],
  recommendationRecords: [
    {
      id: 'rec-1',
      organizationId: 1,
      recommendationType: 'review_supplier_rule',
      policyChangeType: 'supplier_rule_change',
      scopeType: 'supplier',
      scopeId: '301',
      currentPolicyMode: 'stock',
      proposedPolicyMode: 'stock',
      anomalyIds: ['an-2', 'an-4'],
      description: 'Reducir cobertura y revisar MOQ/calendario del proveedor Metal Group.',
      proposedChanges: ['Reducir frecuencia objetivo de 45 a 21 días', 'Revisar MOQ en artículos con exceso recurrente'],
      expectedImpact: 'Menor capital inmovilizado y menos compras forzadas por proveedor.',
      expectedInventoryImpact: '-121.000 € capital inmovilizado estimado',
      expectedServiceImpact: 'Sin impacto negativo esperado en servicio',
      expectedProductionImpact: null,
      expectedOperationalImpact: 'Menor carga de revisiones manuales en compras',
      expectedCommercialImpact: null,
      confidenceScore: 0.84,
      status: 'pending_approval',
      createdAt: new Date(Date.now() - 34 * 60_000).toISOString(),
      approvedBy: null,
      approvedAt: null,
      appliedAt: null,
      expiresAt: null,
      reviewAt: new Date(Date.now() + 20 * 24 * 60 * 60_000).toISOString(),
      rejectionReason: null,
      approvalComment: null,
    },
    {
      id: 'rec-2',
      organizationId: 1,
      recommendationType: 'recalibrate_policy',
      policyChangeType: 'parameter_recalibration',
      scopeType: 'abc_xyz',
      scopeId: 'A/Z',
      currentPolicyMode: 'stock',
      proposedPolicyMode: 'stock',
      anomalyIds: ['an-1'],
      description: 'Aumentar safety stock en 42 SKUs A/Z con riesgo de rotura futura.',
      proposedChanges: ['Subir safety stock mínimo en SKUs A/Z', 'Recalcular ROP con lead time real'],
      expectedImpact: 'Reducir roturas y venta perdida en referencias críticas.',
      expectedInventoryImpact: '+21.500 € inventario',
      expectedServiceImpact: '-14 roturas / mes estimadas',
      expectedProductionImpact: null,
      expectedOperationalImpact: '-8 pedidos urgentes / mes',
      expectedCommercialImpact: 'Menor venta perdida en clientes estratégicos',
      confidenceScore: 0.81,
      status: 'draft',
      createdAt: new Date(Date.now() - 60 * 60_000).toISOString(),
      approvedBy: null,
      approvedAt: null,
      appliedAt: null,
      expiresAt: null,
      reviewAt: new Date(Date.now() + 30 * 24 * 60 * 60_000).toISOString(),
      rejectionReason: null,
      approvalComment: null,
    },
    {
      id: 'rec-3',
      organizationId: 1,
      recommendationType: 'review_forecast',
      policyChangeType: 'sop_review',
      scopeType: 'family',
      scopeId: '41',
      currentPolicyMode: 'stock',
      proposedPolicyMode: 'stock',
      anomalyIds: ['an-1'],
      description: 'Enviar a revisión el forecast de la familia Frenos por desviación con venta real.',
      proposedChanges: ['Revisar demanda base en S&OP', 'Aplicar protección temporal mientras se valida forecast'],
      expectedImpact: 'Alinear política con demanda real y reducir venta perdida.',
      expectedInventoryImpact: '+9.000 € cobertura temporal',
      expectedServiceImpact: '-11 SKUs con riesgo de rotura',
      expectedProductionImpact: null,
      expectedOperationalImpact: 'Menos compras urgentes derivadas de forecast bajo',
      expectedCommercialImpact: 'Mejora de cumplimiento en familia crítica',
      confidenceScore: 0.79,
      status: 'monitoring',
      createdAt: new Date(Date.now() - 2 * 60 * 60_000).toISOString(),
      approvedBy: 'laura.m',
      approvedAt: new Date(Date.now() - 90 * 60_000).toISOString(),
      appliedAt: null,
      expiresAt: null,
      reviewAt: new Date(Date.now() + 14 * 24 * 60 * 60_000).toISOString(),
      rejectionReason: null,
      approvalComment: 'Validar con S&OP antes de aplicar cambios masivos.',
    },
    {
      id: 'rec-4',
      organizationId: 1,
      recommendationType: 'review_supplier_rule',
      policyChangeType: 'supplier_rule_change',
      scopeType: 'supplier',
      scopeId: '512',
      currentPolicyMode: 'bajo_pedido',
      proposedPolicyMode: 'stock_temporal',
      anomalyIds: ['an-5'],
      description: 'Agrupar compras y revisar MOQ para el proveedor X en componentes comunes.',
      proposedChanges: ['Simular stock temporal de componentes baratos', 'Agrupar demanda por BOM'],
      expectedImpact: 'Reducir exceso por MOQ y bloquear menos órdenes productivas.',
      expectedInventoryImpact: '+4.800 € buffer temporal',
      expectedServiceImpact: 'Sin impacto negativo relevante',
      expectedProductionImpact: '-2 órdenes bloqueadas / mes',
      expectedOperationalImpact: '-6 compras urgentes / mes',
      expectedCommercialImpact: null,
      confidenceScore: 0.74,
      status: 'approved',
      createdAt: new Date(Date.now() - 5 * 60 * 60_000).toISOString(),
      approvedBy: 'gema.admin',
      approvedAt: new Date(Date.now() - 3 * 60 * 60_000).toISOString(),
      appliedAt: null,
      expiresAt: new Date(Date.now() + 45 * 24 * 60 * 60_000).toISOString(),
      reviewAt: new Date(Date.now() + 21 * 24 * 60 * 60_000).toISOString(),
      rejectionReason: null,
      approvalComment: 'Aplicar primero en proveedor con mayor impacto de MOQ.',
    },
  ],
};

export const inventoryPolicyControlStoresDashboardMock: InventoryPolicyControlDashboardPayload = {
  kpi: {
    id: 'snap-store-1',
    organizationId: 1,
    warehouseId: null,
    snapshotAt: new Date().toISOString(),
    totalInventoryValueEur: 612_480,
    monthsOfInventory: 2.8,
    skusNoStockCount: 42,
    skusOverstockCount: 27,
    policiesToReviewCount: 37,
    totalInventoryValuePctVsPrev: 4.1,
    monthsOfInventoryPctVsPrev: 3.8,
    skusNoStockPctVsPrev: 11.2,
    skusOverstockPctVsPrev: 5.4,
  },
  health: {
    overallScore: 78,
    overallLabelKey: 'good',
    segments: [
      { statusKey: 'obsolete', statusLabel: 'Obsoleto', statusOrder: 1, percentage: 8, count: 0 },
      { statusKey: 'overstock', statusLabel: 'Sobreinventario', statusOrder: 2, percentage: 12, count: 0 },
      { statusKey: 'optimal', statusLabel: 'Óptimo', statusOrder: 3, percentage: 54, count: 0 },
      { statusKey: 'under_minimum', statusLabel: 'Bajo mínimos', statusOrder: 4, percentage: 11, count: 0 },
      { statusKey: 'no_stock', statusLabel: 'Sin stock', statusOrder: 5, percentage: 15, count: 0 },
    ],
  },
  topProblems: [
    {
      id: 'stores-tp-1',
      problemCode: 'SERV-001',
      problemLabel: 'Sin stock actual',
      affectedPoliciesCount: 18,
      economicImpact: 6_480,
      impactLabel: '6.480 € venta perdida',
      priority: 'alta',
      detectionCriteria: 'Stock actual <= 0 con venta o forecast positivo',
      probableCause: 'Mínimo de tienda insuficiente para cubrir hasta la siguiente reposición',
      recommendedAction: 'Subir cobertura mínima o adelantar reposición',
    },
    {
      id: 'stores-tp-2',
      problemCode: 'SERV-002',
      problemLabel: 'Riesgo de rotura futura',
      affectedPoliciesCount: 14,
      economicImpact: 4_920,
      impactLabel: '4.920 € riesgo servicio',
      priority: 'alta',
      detectionCriteria: 'Fecha de rotura estimada < fecha de reposición',
      probableCause: 'Cobertura de tienda insuficiente hasta la siguiente reposición',
      recommendedAction: 'Subir ROP/cobertura o aumentar frecuencia de envío',
    },
    {
      id: 'stores-tp-3',
      problemCode: 'EXC-001',
      problemLabel: 'Sobrestock',
      affectedPoliciesCount: 11,
      economicImpact: 15_740,
      impactLabel: '15.740 € capital inmovilizado',
      priority: 'media',
      detectionCriteria: 'Cobertura actual > cobertura objetivo * umbral',
      probableCause: 'Cobertura de tienda por encima del objetivo',
      recommendedAction: 'Reducir mínimos o cobertura objetivo',
    },
    {
      id: 'stores-tp-4',
      problemCode: 'OPS-001',
      problemLabel: 'Pedidos manuales recurrentes',
      affectedPoliciesCount: 9,
      economicImpact: 2_150,
      impactLabel: '2.150 € carga operativa',
      priority: 'media',
      detectionCriteria: 'Pedidos manuales >= umbral con política automática activa',
      probableCause: 'La política no refleja bien la demanda local o el calendario de reposición',
      recommendedAction: 'Revisar reglas locales o frecuencia',
    },
    {
      id: 'stores-tp-5',
      problemCode: 'FREQ-004',
      problemLabel: 'Cobertura objetivo incoherente',
      affectedPoliciesCount: 8,
      economicImpact: 3_860,
      impactLabel: '3.860 € exceso y faltantes',
      priority: 'media',
      detectionCriteria: 'Cobertura objetivo fuera de límites',
      probableCause: 'La combinación de frecuencia, lead time y stock de seguridad genera una cobertura incoherente',
      recommendedAction: 'Ajustar frecuencia y parámetros de cobertura',
    },
  ],
  problemInsights: [
    {
      id: 'stores-tp-1',
      problemCode: 'SERV-001',
      problemLabel: 'Sin stock actual',
      priority: 'alta',
      affectedPoliciesCount: 18,
      affectedProductsCount: 11,
      affectedLocationsCount: 6,
      estimatedEconomicImpact: 6_480,
      estimatedImpactLabel: '6.480 € venta perdida',
      summary: 'Se detectan 18 políticas activas con stock cero en 11 productos y 6 tiendas. El patrón se concentra en accesorios de alta rotación reabastecidos desde Central Levante y Madrid.',
      detectionCriteria: 'Stock actual <= 0 con venta reciente o forecast positivo.',
      primaryCause: 'Mínimo de tienda insuficiente para cubrir hasta la siguiente reposición desde central.',
      secondaryCauses: [
        'Frecuencia de reposición insuficiente',
        'Lead time interno superior al supuesto',
        'Falta puntual en central sobre referencias clave',
      ],
      recommendedAction: 'Subir cobertura mínima o adelantar reposición en las tiendas más afectadas.',
      simulationPossible: true,
      relatedRecommendationIds: ['stores-rec-1', 'stores-rec-2'],
      concentrationDimensions: [
        { id: 'stores-serv-dim-1', dimensionLabelKey: 'pointOfSale', criticalElement: 'Barcelona Centro', impactLabel: '5 políticas' },
        { id: 'stores-serv-dim-2', dimensionLabelKey: 'family', criticalElement: 'Accesorios móviles', impactLabel: '2.940 €' },
        { id: 'stores-serv-dim-3', dimensionLabelKey: 'abcXyz', criticalElement: 'A / X', impactLabel: '9 políticas' },
      ],
      affectedPolicies: [
        {
          id: 'stores-serv-pol-1',
          productCode: 'ST22001',
          productName: 'Cargador USB-C 30W',
          pointOfSale: 'Barcelona Centro',
          supplier: 'Central Levante',
          currentStock: 0,
          reorderPoint: 6,
          safetyStock: 2,
          leadTimeDays: 2,
          suggestedAction: 'Subir minimo de 6 a 8 ud',
        },
        {
          id: 'stores-serv-pol-2',
          productCode: 'ST22014',
          productName: 'Funda iPhone 15',
          pointOfSale: 'Valencia Colon',
          supplier: 'Central Levante',
          currentStock: 0,
          reorderPoint: 6,
          safetyStock: 3,
          leadTimeDays: 2,
          suggestedAction: 'Subir frecuencia de reposicion de semanal a diaria',
        },
        {
          id: 'stores-serv-pol-3',
          productCode: 'ST22110',
          productName: 'Auriculares TWS Lite',
          pointOfSale: 'Madrid Goya',
          supplier: 'Central Madrid',
          currentStock: 0,
          reorderPoint: 5,
          safetyStock: 2,
          leadTimeDays: 1,
          suggestedAction: 'Subir minimo de 5 a 7 ud',
        },
        {
          id: 'stores-serv-pol-4',
          productCode: 'ST22142',
          productName: 'Cable Lightning 1m',
          pointOfSale: 'Barcelona Centro',
          supplier: 'Central Levante',
          currentStock: 0,
          reorderPoint: 7,
          safetyStock: 2,
          leadTimeDays: 2,
          suggestedAction: 'Subir SS de 2 a 4 ud',
        },
        {
          id: 'stores-serv-pol-5',
          productCode: 'ST22163',
          productName: 'Protector iPhone 15',
          pointOfSale: 'Sevilla Centro',
          supplier: 'Central Sur',
          currentStock: 0,
          reorderPoint: 5,
          safetyStock: 2,
          leadTimeDays: 2,
          suggestedAction: 'Subir minimo de 5 a 7 ud',
        },
        {
          id: 'stores-serv-pol-6',
          productCode: 'ST22178',
          productName: 'Adaptador USB-C a Jack',
          pointOfSale: 'Bilbao Gran Via',
          supplier: 'Central Norte',
          currentStock: 0,
          reorderPoint: 4,
          safetyStock: 1,
          leadTimeDays: 2,
          suggestedAction: 'Subir minimo de 4 a 6 ud',
        },
      ],
    },
    {
      id: 'stores-tp-2',
      problemCode: 'SERV-002',
      problemLabel: 'Riesgo de rotura futura',
      priority: 'alta',
      affectedPoliciesCount: 14,
      affectedProductsCount: 9,
      affectedLocationsCount: 5,
      estimatedEconomicImpact: 4_920,
      estimatedImpactLabel: '4.920 € riesgo servicio',
      summary: 'Hay 14 políticas con stock todavía positivo, pero con cobertura insuficiente para llegar a la siguiente transferencia desde central. Afectan sobre todo a audio portátil y wearables de alta rotación.',
      detectionCriteria: 'Fecha de rotura estimada < fecha de reposición posible.',
      primaryCause: 'Cobertura de tienda insuficiente para llegar a la siguiente reposición desde central.',
      secondaryCauses: [
        'Frecuencia de envío insuficiente',
        'Lead time interno real superior al parametrizado',
        'Picos de demanda no absorbidos por el mínimo actual',
      ],
      recommendedAction: 'Subir ROP o cobertura temporal y aumentar frecuencia donde haga falta.',
      simulationPossible: true,
      relatedRecommendationIds: ['stores-rec-1'],
      concentrationDimensions: [
        { id: 'stores-risk-dim-1', dimensionLabelKey: 'pointOfSale', criticalElement: 'Sevilla Centro', impactLabel: '4 políticas' },
        { id: 'stores-risk-dim-2', dimensionLabelKey: 'salesPotential', criticalElement: 'Alta rotación', impactLabel: '6 políticas' },
        { id: 'stores-risk-dim-3', dimensionLabelKey: 'family', criticalElement: 'Audio portátil', impactLabel: '1.960 €' },
      ],
      affectedPolicies: [
        {
          id: 'stores-risk-pol-1',
          productCode: 'ST31001',
          productName: 'Auriculares Noise Cancel',
          pointOfSale: 'Sevilla Centro',
          supplier: 'Central Sur',
          currentStock: 3,
          reorderPoint: 6,
          safetyStock: 2,
          leadTimeDays: 3,
          suggestedAction: 'Subir cobertura de 3 a 6 dias',
        },
        {
          id: 'stores-risk-pol-2',
          productCode: 'ST31045',
          productName: 'Altavoz Bluetooth Mini',
          pointOfSale: 'Malaga Larios',
          supplier: 'Central Sur',
          currentStock: 4,
          reorderPoint: 6,
          safetyStock: 2,
          leadTimeDays: 3,
          suggestedAction: 'Subir ROP de 6 a 8 ud',
        },
        {
          id: 'stores-risk-pol-3',
          productCode: 'ST31100',
          productName: 'Smartwatch Fit Basic',
          pointOfSale: 'Murcia Centro',
          supplier: 'Central Levante',
          currentStock: 2,
          reorderPoint: 5,
          safetyStock: 2,
          leadTimeDays: 2,
          suggestedAction: 'Subir SS de 2 a 3 ud',
        },
        {
          id: 'stores-risk-pol-4',
          productCode: 'ST31118',
          productName: 'Altavoz portátil Clip',
          pointOfSale: 'Sevilla Centro',
          supplier: 'Central Sur',
          currentStock: 2,
          reorderPoint: 5,
          safetyStock: 2,
          leadTimeDays: 3,
          suggestedAction: 'Subir ROP de 5 a 7 ud',
        },
        {
          id: 'stores-risk-pol-5',
          productCode: 'ST31126',
          productName: 'Pulsera actividad Basic',
          pointOfSale: 'Barcelona Centro',
          supplier: 'Central Levante',
          currentStock: 3,
          reorderPoint: 5,
          safetyStock: 2,
          leadTimeDays: 2,
          suggestedAction: 'Subir cobertura de 2 a 4 dias',
        },
        {
          id: 'stores-risk-pol-6',
          productCode: 'ST31134',
          productName: 'Auricular gaming wireless',
          pointOfSale: 'Valencia Colon',
          supplier: 'Central Levante',
          currentStock: 2,
          reorderPoint: 5,
          safetyStock: 2,
          leadTimeDays: 2,
          suggestedAction: 'Subir SS de 2 a 4 ud',
        },
      ],
    },
    {
      id: 'stores-tp-3',
      problemCode: 'EXC-001',
      problemLabel: 'Sobrestock',
      priority: 'media',
      affectedPoliciesCount: 11,
      affectedProductsCount: 8,
      affectedLocationsCount: 4,
      estimatedEconomicImpact: 15_740,
      estimatedImpactLabel: '15.740 € capital inmovilizado',
      summary: 'El sobrestock se concentra en 11 políticas de 8 productos con rotación menor a la esperada. Predominan accesorios gaming y cables premium enviados con una cadencia demasiado alta para la venta real.',
      detectionCriteria: 'Cobertura actual > cobertura objetivo * umbral.',
      primaryCause: 'Cobertura de tienda por encima del objetivo definido para su rotación.',
      secondaryCauses: [
        'Mínimos homogéneos para tiendas con distinta demanda',
        'Forecast local superior a la venta real',
        'Frecuencia de reposición demasiado alta',
      ],
      recommendedAction: 'Reducir mínimos o cobertura objetivo en las tiendas lentas.',
      simulationPossible: true,
      relatedRecommendationIds: ['stores-rec-3'],
      concentrationDimensions: [
        { id: 'stores-over-dim-1', dimensionLabelKey: 'pointOfSale', criticalElement: 'Bilbao Gran Via', impactLabel: '4 políticas' },
        { id: 'stores-over-dim-2', dimensionLabelKey: 'family', criticalElement: 'Accesorios gaming', impactLabel: '6.420 €' },
        { id: 'stores-over-dim-3', dimensionLabelKey: 'product', criticalElement: 'Cables premium', impactLabel: '3 políticas' },
      ],
      affectedPolicies: [
        {
          id: 'stores-over-pol-1',
          productCode: 'ST41010',
          productName: 'Cable HDMI 2.1 Premium',
          pointOfSale: 'Bilbao Gran Via',
          supplier: 'Central Norte',
          currentStock: 88,
          reorderPoint: 12,
          safetyStock: 5,
          leadTimeDays: 2,
          suggestedAction: 'Reducir mínimo a 6 ud',
        },
        {
          id: 'stores-over-pol-2',
          productCode: 'ST41022',
          productName: 'Mouse gaming RGB',
          pointOfSale: 'Oviedo Centro',
          supplier: 'Central Norte',
          currentStock: 64,
          reorderPoint: 10,
          safetyStock: 4,
          leadTimeDays: 2,
          suggestedAction: 'Bajar frecuencia de reposicion de diaria a semanal',
        },
        {
          id: 'stores-over-pol-3',
          productCode: 'ST41038',
          productName: 'Teclado mecanico base',
          pointOfSale: 'Santander Centro',
          supplier: 'Central Norte',
          currentStock: 51,
          reorderPoint: 9,
          safetyStock: 4,
          leadTimeDays: 2,
          suggestedAction: 'Bajar minimo de 9 a 6 ud',
        },
        {
          id: 'stores-over-pol-4',
          productCode: 'ST41044',
          productName: 'Hub USB 6 puertos',
          pointOfSale: 'Bilbao Gran Via',
          supplier: 'Central Norte',
          currentStock: 37,
          reorderPoint: 7,
          safetyStock: 3,
          leadTimeDays: 2,
          suggestedAction: 'Bajar minimo de 7 a 5 ud',
        },
        {
          id: 'stores-over-pol-5',
          productCode: 'ST41057',
          productName: 'Ratón gaming entry',
          pointOfSale: 'Oviedo Centro',
          supplier: 'Central Norte',
          currentStock: 29,
          reorderPoint: 6,
          safetyStock: 2,
          leadTimeDays: 2,
          suggestedAction: 'Reducir cobertura objetivo de 18 a 10 dias',
        },
        {
          id: 'stores-over-pol-6',
          productCode: 'ST41063',
          productName: 'Cable DisplayPort 2m',
          pointOfSale: 'Santander Centro',
          supplier: 'Central Norte',
          currentStock: 26,
          reorderPoint: 5,
          safetyStock: 2,
          leadTimeDays: 2,
          suggestedAction: 'Bajar frecuencia de reposicion de semanal a quincenal',
        },
      ],
    },
    {
      id: 'stores-tp-4',
      problemCode: 'OPS-001',
      problemLabel: 'Pedidos manuales recurrentes',
      priority: 'media',
      affectedPoliciesCount: 9,
      affectedProductsCount: 7,
      affectedLocationsCount: 4,
      estimatedEconomicImpact: 2_150,
      estimatedImpactLabel: '2.150 € carga operativa',
      summary: 'En 9 políticas de 7 productos la tienda está corrigiendo manualmente la sugerencia una y otra vez. El patrón apunta más a un mal ajuste de mínimos y frecuencia que a incidencias aisladas.',
      detectionCriteria: 'Pedidos manuales >= umbral con política automática activa.',
      primaryCause: 'La política de tienda no refleja bien la demanda local o el calendario de reposición.',
      secondaryCauses: [
        'Mínimos no adaptados al formato de tienda',
        'Frecuencia de transferencia demasiado espaciada',
        'Demanda local no capturada',
      ],
      recommendedAction: 'Revisar reglas locales y convertir patrones manuales en ajustes gobernados.',
      simulationPossible: false,
      relatedRecommendationIds: ['stores-rec-4'],
      concentrationDimensions: [
        { id: 'stores-ops-dim-1', dimensionLabelKey: 'pointOfSale', criticalElement: 'Valencia Colon', impactLabel: '3 políticas' },
        { id: 'stores-ops-dim-2', dimensionLabelKey: 'family', criticalElement: 'Accesorios smartphone', impactLabel: '5 políticas' },
        { id: 'stores-ops-dim-3', dimensionLabelKey: 'abcXyz', criticalElement: 'B / X', impactLabel: '4 políticas' },
      ],
      affectedPolicies: [
        {
          id: 'stores-ops-pol-1',
          productCode: 'ST51001',
          productName: 'Protector vidrio universal',
          pointOfSale: 'Valencia Colon',
          supplier: 'Central Levante',
          currentStock: 7,
          reorderPoint: 4,
          safetyStock: 1,
          leadTimeDays: 1,
          suggestedAction: 'Subir minimo de 4 a 6 ud',
        },
        {
          id: 'stores-ops-pol-2',
          productCode: 'ST51009',
          productName: 'Cable lightning 2m',
          pointOfSale: 'Barcelona Centro',
          supplier: 'Central Levante',
          currentStock: 9,
          reorderPoint: 5,
          safetyStock: 2,
          leadTimeDays: 2,
          suggestedAction: 'Subir minimo de 5 a 7 ud',
        },
        {
          id: 'stores-ops-pol-3',
          productCode: 'ST51011',
          productName: 'Soporte coche magnetico',
          pointOfSale: 'Madrid Goya',
          supplier: 'Central Madrid',
          currentStock: 6,
          reorderPoint: 3,
          safetyStock: 1,
          leadTimeDays: 1,
          suggestedAction: 'Subir ROP de 3 a 5 ud',
        },
        {
          id: 'stores-ops-pol-4',
          productCode: 'ST51026',
          productName: 'Soporte móvil escritorio',
          pointOfSale: 'Valencia Colon',
          supplier: 'Central Levante',
          currentStock: 8,
          reorderPoint: 4,
          safetyStock: 1,
          leadTimeDays: 1,
          suggestedAction: 'Subir frecuencia de reposicion de semanal a 2 veces/semana',
        },
        {
          id: 'stores-ops-pol-5',
          productCode: 'ST51031',
          productName: 'Cable USB-C 2m',
          pointOfSale: 'Sevilla Centro',
          supplier: 'Central Sur',
          currentStock: 5,
          reorderPoint: 3,
          safetyStock: 1,
          leadTimeDays: 2,
          suggestedAction: 'Subir minimo de 3 a 5 ud',
        },
        {
          id: 'stores-ops-pol-6',
          productCode: 'ST51044',
          productName: 'Cargador coche doble USB',
          pointOfSale: 'Malaga Larios',
          supplier: 'Central Sur',
          currentStock: 6,
          reorderPoint: 3,
          safetyStock: 1,
          leadTimeDays: 2,
          suggestedAction: 'Subir frecuencia de reposicion de semanal a 2 veces/semana',
        },
      ],
    },
    {
      id: 'stores-tp-5',
      problemCode: 'FREQ-004',
      problemLabel: 'Cobertura objetivo incoherente',
      priority: 'media',
      affectedPoliciesCount: 8,
      affectedProductsCount: 6,
      affectedLocationsCount: 5,
      estimatedEconomicImpact: 3_860,
      estimatedImpactLabel: '3.860 € exceso y faltantes',
      summary: 'En 8 políticas la frecuencia de reposición desde central no está bien calibrada: en unas tiendas llega tarde y en otras demasiado pronto. El patrón se concentra en tiendas urbanas con demanda más volátil.',
      detectionCriteria: 'Cobertura objetivo fuera de los límites aceptables.',
      primaryCause: 'La combinación de frecuencia, lead time y stock de seguridad genera una cobertura incoherente.',
      secondaryCauses: [
        'Calendario uniforme para tiendas heterogéneas',
        'Lead time interno variable',
        'Falta de segmentación por cluster o potencial',
      ],
      recommendedAction: 'Ajustar frecuencia y parámetros de cobertura por cluster.',
      simulationPossible: true,
      relatedRecommendationIds: ['stores-rec-1', 'stores-rec-3'],
      concentrationDimensions: [
        { id: 'stores-freq-dim-1', dimensionLabelKey: 'pointOfSale', criticalElement: 'Cluster urbano A', impactLabel: '5 políticas' },
        { id: 'stores-freq-dim-2', dimensionLabelKey: 'salesPotential', criticalElement: 'Alta rotación', impactLabel: '4 políticas' },
        { id: 'stores-freq-dim-3', dimensionLabelKey: 'family', criticalElement: 'Audio portátil', impactLabel: '1.480 €' },
      ],
      affectedPolicies: [
        {
          id: 'stores-freq-pol-1',
          productCode: 'ST61002',
          productName: 'Cascos on-ear color',
          pointOfSale: 'Barcelona Centro',
          supplier: 'Central Levante',
          currentStock: 11,
          reorderPoint: 6,
          safetyStock: 2,
          leadTimeDays: 2,
          suggestedAction: 'Subir frecuencia de reposicion de 2 a 3 envios/semana',
        },
        {
          id: 'stores-freq-pol-2',
          productCode: 'ST61018',
          productName: 'Powerbank 10k',
          pointOfSale: 'Alicante Centro',
          supplier: 'Central Levante',
          currentStock: 15,
          reorderPoint: 7,
          safetyStock: 2,
          leadTimeDays: 2,
          suggestedAction: 'Bajar frecuencia de reposicion de 3 a 2 envios/semana',
        },
        {
          id: 'stores-freq-pol-3',
          productCode: 'ST61041',
          productName: 'Mini altavoz portátil',
          pointOfSale: 'Madrid Goya',
          supplier: 'Central Madrid',
          currentStock: 10,
          reorderPoint: 5,
          safetyStock: 2,
          leadTimeDays: 1,
          suggestedAction: 'Bajar frecuencia de reposicion de semanal a quincenal',
        },
        {
          id: 'stores-freq-pol-4',
          productCode: 'ST61047',
          productName: 'Mini altavoz waterproof',
          pointOfSale: 'Valencia Colon',
          supplier: 'Central Levante',
          currentStock: 7,
          reorderPoint: 6,
          safetyStock: 2,
          leadTimeDays: 2,
          suggestedAction: 'Subir frecuencia de reposicion de semanal a 2 veces/semana',
        },
        {
          id: 'stores-freq-pol-5',
          productCode: 'ST61053',
          productName: 'Auricular sport basic',
          pointOfSale: 'Sevilla Centro',
          supplier: 'Central Sur',
          currentStock: 9,
          reorderPoint: 5,
          safetyStock: 2,
          leadTimeDays: 3,
          suggestedAction: 'Subir frecuencia de reposicion de semanal a 2 veces/semana',
        },
        {
          id: 'stores-freq-pol-6',
          productCode: 'ST61060',
          productName: 'Altavoz smart mini',
          pointOfSale: 'Bilbao Gran Via',
          supplier: 'Central Norte',
          currentStock: 8,
          reorderPoint: 5,
          safetyStock: 2,
          leadTimeDays: 2,
          suggestedAction: 'Subir frecuencia de reposicion de semanal a 2 veces/semana',
        },
      ],
    },
  ],
  dimensions: [
    {
      id: 'stores-dim-1',
      dimensionKey: 'warehouse',
      dimensionLabelKey: 'pointOfSale',
      healthLabel: 'mala',
      mainProblem: 'Roturas y riesgo de rotura en tiendas urbanas',
      alertCount: 22,
      impactValue: 11_400,
      impactUnit: 'eur',
    },
    {
      id: 'stores-dim-2',
      dimensionKey: 'family',
      dimensionLabelKey: 'family',
      healthLabel: 'regular',
      mainProblem: 'Desajuste entre rotación real y mínimos',
      alertCount: 18,
      impactValue: 9_800,
      impactUnit: 'eur',
    },
    {
      id: 'stores-dim-3',
      dimensionKey: 'segment',
      dimensionLabelKey: 'salesPotential',
      healthLabel: 'regular',
      mainProblem: 'Reposición uniforme para potenciales distintos',
      alertCount: 15,
      impactValue: 5_400,
      impactUnit: 'eur',
    },
    {
      id: 'stores-dim-4',
      dimensionKey: 'product',
      dimensionLabelKey: 'product',
      healthLabel: 'regular',
      mainProblem: 'Exceso en referencias estándar de baja salida',
      alertCount: 11,
      impactValue: 15_740,
      impactUnit: 'eur',
    },
    {
      id: 'stores-dim-5',
      dimensionKey: 'abc_xyz',
      dimensionLabelKey: 'abcXyz',
      healthLabel: 'buena',
      mainProblem: 'Protección insuficiente en segmentos A/X',
      alertCount: 9,
      impactValue: 9,
      impactUnit: 'skus',
    },
  ],
  recommendationsInbox: [
    {
      id: 'stores-rec-inbox-1',
      sortOrder: 1,
      title: 'Ajuste temporal · Cluster tiendas · Urbano A',
      priority: 'alta',
      recommendationId: 'stores-rec-1',
      sourceAnomalyCodes: ['SERV-001', 'SERV-002', 'FREQ-004'],
      status: 'pending_approval',
      scopeLabel: 'Cluster tiendas · Urbano A',
      expectedImpactLabel: 'Reducir roturas y venta perdida en flagship y urbanas',
      createdAt: new Date(Date.now() - 24 * 60_000).toISOString(),
    },
    {
      id: 'stores-rec-inbox-2',
      sortOrder: 2,
      title: 'Crear stock mínimo · Tienda · Barcelona Centro',
      priority: 'alta',
      recommendationId: 'stores-rec-2',
      sourceAnomalyCodes: ['SERV-001'],
      status: 'draft',
      scopeLabel: 'Tienda · Barcelona Centro',
      expectedImpactLabel: 'Mejorar disponibilidad en referencias de alta rotacion',
      createdAt: new Date(Date.now() - 58 * 60_000).toISOString(),
    },
    {
      id: 'stores-rec-inbox-3',
      sortOrder: 3,
      title: 'Recalibrar política · Familia · Accesorios gaming',
      priority: 'media',
      recommendationId: 'stores-rec-3',
      sourceAnomalyCodes: ['EXC-001', 'FREQ-004'],
      status: 'monitoring',
      scopeLabel: 'Familia · Accesorios gaming',
      expectedImpactLabel: 'Liberar capital en tienda sin degradar servicio',
      createdAt: new Date(Date.now() - 3 * 60 * 60_000).toISOString(),
    },
    {
      id: 'stores-rec-inbox-4',
      sortOrder: 4,
      title: 'Solo seguimiento · Cluster tiendas · Flagship',
      priority: 'media',
      recommendationId: 'stores-rec-4',
      sourceAnomalyCodes: ['OPS-001'],
      status: 'approved',
      scopeLabel: 'Cluster tiendas · Flagship',
      expectedImpactLabel: 'Reducir intervenciones manuales y urgencias locales',
      createdAt: new Date(Date.now() - 6 * 60 * 60_000).toISOString(),
    },
  ],
  anomalyRecords: [
    {
      id: 'stores-an-1',
      organizationId: 1,
      warehouseId: 101,
      productId: 501,
      supplierId: null,
      familyId: 10,
      scopeType: 'product',
      scopeId: '501|101|central-levante',
      itemRole: 'finished_product',
      currentPolicyMode: 'stock',
      suggestedPolicyMode: 'stock',
      anomalyCode: 'SERV-001',
      anomalyCategory: 'service',
      severity: 'alta',
      riskScore: 88,
      impactType: 'service',
      primaryCause: 'La tienda se queda sin stock antes de la siguiente transferencia interna',
      secondaryCauses: ['Stock minimo insuficiente', 'Central con suministro tardio'],
      confidenceScore: 0.9,
      detectedAt: new Date().toISOString(),
      status: 'open',
      currentValue: 'Stock actual 0 ud',
      expectedValue: 'Minimo sugerido 8 ud',
      differenceValue: '-8 ud',
      economicImpact: 24_600,
      serviceImpact: 16,
      operationalImpact: 3,
      productionImpact: null,
      blockedOrderValue: null,
      affectedProductionOrdersCount: null,
      affectedSalesOrdersCount: 14,
      bomUsageCount: null,
      recommendedAction: 'Subir minimo o aumentar frecuencia de transferencia',
      simulationAvailable: true,
    },
    {
      id: 'stores-an-2',
      organizationId: 1,
      warehouseId: 102,
      productId: 601,
      supplierId: null,
      familyId: 12,
      scopeType: 'product',
      scopeId: '601|102|central-sur',
      itemRole: 'finished_product',
      currentPolicyMode: 'stock',
      suggestedPolicyMode: 'stock',
      anomalyCode: 'SERV-002',
      anomalyCategory: 'service',
      severity: 'alta',
      riskScore: 83,
      impactType: 'service',
      primaryCause: 'El stock de tienda se agotara antes de la siguiente reposicion interna',
      secondaryCauses: ['Frecuencia insuficiente', 'Demanda local acelerada'],
      confidenceScore: 0.85,
      detectedAt: new Date().toISOString(),
      status: 'open',
      currentValue: 'Cobertura actual 1,4 dias',
      expectedValue: 'Cobertura minima 3 dias',
      differenceValue: '-1,6 dias',
      economicImpact: 17_800,
      serviceImpact: 12,
      operationalImpact: 2,
      productionImpact: null,
      blockedOrderValue: null,
      affectedProductionOrdersCount: null,
      affectedSalesOrdersCount: 10,
      bomUsageCount: null,
      recommendedAction: 'Aumentar cobertura temporal o anticipar envio',
      simulationAvailable: true,
    },
    {
      id: 'stores-an-3',
      organizationId: 1,
      warehouseId: 103,
      productId: 701,
      supplierId: null,
      familyId: 14,
      scopeType: 'product',
      scopeId: '701|103|central-norte',
      itemRole: 'finished_product',
      currentPolicyMode: 'stock',
      suggestedPolicyMode: 'stock',
      anomalyCode: 'EXC-001',
      anomalyCategory: 'excess_inventory',
      severity: 'media',
      riskScore: 67,
      impactType: 'capital',
      primaryCause: 'La tienda mantiene mas cobertura de la necesaria',
      secondaryCauses: ['Minimos homogeneos', 'Forecast local alto'],
      confidenceScore: 0.82,
      detectedAt: new Date().toISOString(),
      status: 'reviewed',
      currentValue: 'Cobertura actual 27 dias',
      expectedValue: 'Cobertura objetivo 11 dias',
      differenceValue: '+16 dias',
      economicImpact: 48_900,
      serviceImpact: 0,
      operationalImpact: 1,
      productionImpact: null,
      blockedOrderValue: null,
      affectedProductionOrdersCount: null,
      affectedSalesOrdersCount: 0,
      bomUsageCount: null,
      recommendedAction: 'Reducir minimos o frecuencia de envio',
      simulationAvailable: true,
    },
    {
      id: 'stores-an-4',
      organizationId: 1,
      warehouseId: 101,
      productId: 801,
      supplierId: null,
      familyId: 15,
      scopeType: 'product',
      scopeId: '801|101|central-levante',
      itemRole: 'finished_product',
      currentPolicyMode: 'stock',
      suggestedPolicyMode: 'stock',
      anomalyCode: 'OPS-001',
      anomalyCategory: 'operational_behavior',
      severity: 'media',
      riskScore: 61,
      impactType: 'operational',
      primaryCause: 'La tienda corrige manualmente la sugerencia automatica',
      secondaryCauses: ['Demanda local no modelada', 'Frecuencia insuficiente'],
      confidenceScore: 0.79,
      detectedAt: new Date().toISOString(),
      status: 'open',
      currentValue: '7 pedidos manuales / 30 dias',
      expectedValue: '0-1 pedidos manuales / 30 dias',
      differenceValue: '+6 pedidos',
      economicImpact: 8_900,
      serviceImpact: 2,
      operationalImpact: 7,
      productionImpact: null,
      blockedOrderValue: null,
      affectedProductionOrdersCount: null,
      affectedSalesOrdersCount: 3,
      bomUsageCount: null,
      recommendedAction: 'Revisar reglas por cluster o formato de tienda',
      simulationAvailable: false,
    },
  ],
  recommendationRecords: [
    {
      id: 'stores-rec-1',
      organizationId: 1,
      recommendationType: 'temporary_override',
      policyChangeType: 'temporary_override',
      scopeType: 'segment',
      scopeId: 'Urbano A',
      currentPolicyMode: 'stock',
      proposedPolicyMode: 'stock_temporal',
      anomalyIds: ['stores-an-1', 'stores-an-2'],
      description: 'Aumentar frecuencia de reposicion en tiendas urbanas A con rotacion alta.',
      proposedChanges: ['Pasar de 3 a 6 envios semanales', 'Crear buffer temporal de 2 dias en referencias A/X'],
      expectedImpact: 'Menos roturas y menor venta perdida en tiendas de mayor potencial.',
      expectedInventoryImpact: '+12.400 € inventario en tienda',
      expectedServiceImpact: '-18 roturas semanales estimadas',
      expectedProductionImpact: null,
      expectedOperationalImpact: '-9 urgencias / semana',
      expectedCommercialImpact: 'Mayor disponibilidad en tiendas flagship',
      confidenceScore: 0.83,
      status: 'pending_approval',
      createdAt: new Date(Date.now() - 24 * 60_000).toISOString(),
      approvedBy: null,
      approvedAt: null,
      appliedAt: null,
      expiresAt: new Date(Date.now() + 21 * 24 * 60 * 60_000).toISOString(),
      reviewAt: new Date(Date.now() + 7 * 24 * 60 * 60_000).toISOString(),
      rejectionReason: null,
      approvalComment: null,
    },
    {
      id: 'stores-rec-2',
      organizationId: 1,
      recommendationType: 'create_stock_buffer',
      policyChangeType: 'profile_change',
      scopeType: 'warehouse',
      scopeId: 'Barcelona Centro',
      currentPolicyMode: 'stock',
      proposedPolicyMode: 'stock',
      anomalyIds: ['stores-an-1'],
      description: 'Subir stock minimo en 34 SKUs de Barcelona Centro.',
      proposedChanges: ['Incrementar minimo de 4 a 8 ud en referencias top', 'Revisar parametros para cluster flagship'],
      expectedImpact: 'Mejorar servicio local en referencias de alta salida.',
      expectedInventoryImpact: '+5.100 € inventario tienda',
      expectedServiceImpact: '-11 stockouts / semana',
      expectedProductionImpact: null,
      expectedOperationalImpact: '-4 pedidos urgentes / semana',
      expectedCommercialImpact: 'Mejor experiencia cliente en flagship',
      confidenceScore: 0.8,
      status: 'draft',
      createdAt: new Date(Date.now() - 58 * 60_000).toISOString(),
      approvedBy: null,
      approvedAt: null,
      appliedAt: null,
      expiresAt: null,
      reviewAt: new Date(Date.now() + 14 * 24 * 60 * 60_000).toISOString(),
      rejectionReason: null,
      approvalComment: null,
    },
    {
      id: 'stores-rec-3',
      organizationId: 1,
      recommendationType: 'recalibrate_policy',
      policyChangeType: 'parameter_recalibration',
      scopeType: 'family',
      scopeId: 'Accesorios gaming',
      currentPolicyMode: 'stock',
      proposedPolicyMode: 'stock',
      anomalyIds: ['stores-an-3'],
      description: 'Reducir minimos en tiendas con sobrestock de accesorios.',
      proposedChanges: ['Reducir minimo un 20%', 'Separar tiendas A y B en reglas de cobertura'],
      expectedImpact: 'Liberar capital en tienda sin penalizar servicio.',
      expectedInventoryImpact: '-18.900 € inventario en tienda',
      expectedServiceImpact: 'Sin deterioro relevante esperado',
      expectedProductionImpact: null,
      expectedOperationalImpact: '-2 redistribuciones urgentes / semana',
      expectedCommercialImpact: null,
      confidenceScore: 0.76,
      status: 'monitoring',
      createdAt: new Date(Date.now() - 3 * 60 * 60_000).toISOString(),
      approvedBy: 'gema.admin',
      approvedAt: new Date(Date.now() - 2 * 60 * 60_000).toISOString(),
      appliedAt: null,
      expiresAt: null,
      reviewAt: new Date(Date.now() + 10 * 24 * 60 * 60_000).toISOString(),
      rejectionReason: null,
      approvalComment: 'Seguir 2 semanas antes de aplicar a toda la red.',
    },
    {
      id: 'stores-rec-4',
      organizationId: 1,
      recommendationType: 'monitor_only',
      policyChangeType: 'no_action_monitor',
      scopeType: 'segment',
      scopeId: 'Flagship',
      currentPolicyMode: 'stock',
      proposedPolicyMode: null,
      anomalyIds: ['stores-an-4'],
      description: 'Revisar pedidos manuales por cluster de tienda.',
      proposedChanges: ['Registrar motivos recurrentes de override', 'Definir reglas especificas para flagship'],
      expectedImpact: 'Reducir intervenciones manuales y urgencias locales.',
      expectedInventoryImpact: 'Neutro en inventario',
      expectedServiceImpact: 'Mejora indirecta por mejor ajuste local',
      expectedProductionImpact: null,
      expectedOperationalImpact: '-12 pedidos manuales / mes',
      expectedCommercialImpact: 'Mayor consistencia operativa en tiendas clave',
      confidenceScore: 0.71,
      status: 'approved',
      createdAt: new Date(Date.now() - 6 * 60 * 60_000).toISOString(),
      approvedBy: 'gema.admin',
      approvedAt: new Date(Date.now() - 5 * 60 * 60_000).toISOString(),
      appliedAt: null,
      expiresAt: null,
      reviewAt: new Date(Date.now() + 21 * 24 * 60 * 60_000).toISOString(),
      rejectionReason: null,
      approvalComment: 'Monitorizar antes de cambiar parametros de red.',
    },
  ],
};

attachSuggestedChanges(inventoryPolicyControlDashboardMock);
attachSuggestedChanges(inventoryPolicyControlStoresDashboardMock);

export const inventoryPolicyControlDashboardMocks: Record<
  InventoryPolicyControlScope,
  InventoryPolicyControlDashboardPayload
> = {
  central: inventoryPolicyControlDashboardMock,
  stores: inventoryPolicyControlStoresDashboardMock,
};
